import React, { useEffect, useState } from "react";
import AdminHeader from "./Components/AdminHeader";
import AdminSidebar from "./Components/AdminSidebar";
import AboutUsItemModal from "./Components/AboutUsItemModal";
import api from "../../api";
import { IMAGE_URL } from "../../path";
import "../../styles/Login.scss";

interface AboutUsItem {
    id: number;
    about_us_id: number;
    type: "why_choose" | "different";
    icon: string | null;
    body: string;
    display_order: number;
    created_at: string;
    updated_at: string;
}

interface AboutUs {
    id: number;
    page_id: number;
    image: string | null;
    heading: string | null;
    ending: string | null;
    items: AboutUsItem[];
}

interface PageFeatureCard {
    id: number;
    page_id: number;
    title: string;
    image: string | null;
    imageFile?: File | null;
    body: string;
    icon: string | null;
    link: string | null;
    display_order: number;
    created_at?: string;
    updated_at?: string;
}

const AdminAboutUs = () => {
    const [aboutUs, setAboutUs] =
        useState<AboutUs | null>(null);

    const [image, setImage] =
        useState<File | null>(null);

    const [heading, setHeading] =
        useState("");

    const [ending, setEnding] =
        useState("");

    const [whyChooseItems, setWhyChooseItems] =
        useState<AboutUsItem[]>([]);

    const [differentItems, setDifferentItems] =
        useState<AboutUsItem[]>([]);

    const [featuredCards, setFeaturedCards] =
        useState<PageFeatureCard[]>([]);

    const [loading, setLoading] =
        useState(true);

    const [savingContent, setSavingContent] =
        useState(false);

    const [savingFeatured, setSavingFeatured] =
        useState(false);

    const [movingItemId, setMovingItemId] =
        useState<number | null>(null);

    const [deletingItemId, setDeletingItemId] =
        useState<number | null>(null);

    const [movingFeaturedId, setMovingFeaturedId] =
        useState<number | null>(null);

    const [showItemModal, setShowItemModal] =
        useState(false);

    const [selectedItem, setSelectedItem] =
        useState<AboutUsItem | null>(null);

    const [modalType, setModalType] =
        useState<
            "why_choose" | "different"
        >("why_choose");

    const [error, setError] =
        useState("");

    const [success, setSuccess] =
        useState("");

    useEffect(() => {
        fetchAboutUs();
    }, []);

    const fetchAboutUs = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await api.get(
                "/admin/about-us"
            );

            const data: AboutUs | null =
                response.data.data || null;

            setAboutUs(data);

            if (!data) {
                setHeading("");
                setEnding("");
                setWhyChooseItems([]);
                setDifferentItems([]);
                setFeaturedCards([]);
                return;
            }

            setHeading(
                data.heading || ""
            );

            setEnding(
                data.ending || ""
            );

            const items =
                data.items || [];

            setWhyChooseItems(
                items
                    .filter(
                        (item) =>
                            item.type ===
                            "why_choose"
                    )
                    .sort(
                        (a, b) =>
                            a.display_order -
                            b.display_order
                    )
            );

            setDifferentItems(
                items
                    .filter(
                        (item) =>
                            item.type ===
                            "different"
                    )
                    .sort(
                        (a, b) =>
                            a.display_order -
                            b.display_order
                    )
            );

            await fetchFeaturedCards(
                data.page_id
            );
        } catch (err: any) {
            console.error(
                "Failed to load About Us:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to load About Us content. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchFeaturedCards = async (
        pageId: number
    ) => {
        try {
            const response = await api.get(
                "/admin/page-feature-cards"
            );

            const allCards: PageFeatureCard[] =
                response.data.data || response.data;

            const aboutUsCards = allCards
                .filter(
                    (card) =>
                        card.page_id === pageId
                )
                .sort(
                    (a, b) =>
                        a.display_order -
                        b.display_order
                )
                .map((card) => ({
                    ...card,
                    imageFile: null,
                }));

            setFeaturedCards(
                aboutUsCards
            );
        } catch (err: any) {
            console.error(
                "Failed to load About Us Featured cards:",
                err
            );

            throw err;
        }
    };

    const getFileUrl = (
        path: string | null
    ) => {
        if (!path) {
            return "";
        }

        if (
            path.startsWith("http://") ||
            path.startsWith("https://")
        ) {
            return path;
        }

        return `${IMAGE_URL}/${path}`;
    };

    const handleSaveContent = async () => {
        try {
            setSavingContent(true);
            setError("");
            setSuccess("");

            const formData =
                new FormData();

            formData.append(
                "heading",
                heading
            );

            formData.append(
                "ending",
                ending
            );

            if (image) {
                formData.append(
                    "image",
                    image
                );
            }

            await api.post(
                "/admin/about-us",
                formData
            );

            setImage(null);

            await fetchAboutUs();

            setSuccess(
                "About Us content saved successfully."
            );
        } catch (err: any) {
            handleError(
                err,
                "Unable to save About Us content. Please try again."
            );
        } finally {
            setSavingContent(false);
        }
    };

    const handleAddItem = (
        type:
            | "why_choose"
            | "different"
    ) => {
        setModalType(type);
        setSelectedItem(null);
        setShowItemModal(true);
        setError("");
        setSuccess("");
    };

    const handleEditItem = (
        item: AboutUsItem
    ) => {
        setModalType(item.type);
        setSelectedItem(item);
        setShowItemModal(true);
        setError("");
        setSuccess("");
    };

    const handleCloseModal = () => {
        setShowItemModal(false);
        setSelectedItem(null);
    };

    const handleItemSaved = async () => {
        await fetchAboutUs();

        setSuccess(
            selectedItem
                ? "Item updated successfully."
                : "Item added successfully."
        );
    };

    const handleDeleteItem = async (
        item: AboutUsItem
    ) => {
        const confirmed =
            window.confirm(
                "Are you sure you want to delete this item?"
            );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingItemId(
                item.id
            );

            setError("");
            setSuccess("");

            await api.delete(
                `/admin/about-us/items/${item.id}`
            );

            await fetchAboutUs();

            setSuccess(
                "Item deleted successfully."
            );
        } catch (err: any) {
            handleError(
                err,
                "Unable to delete item. Please try again."
            );
        } finally {
            setDeletingItemId(null);
        }
    };

    const handleMoveItem = async (
        item: AboutUsItem,
        direction: "up" | "down"
    ) => {
        try {
            setMovingItemId(
                item.id
            );

            setError("");
            setSuccess("");

            await api.post(
                `/admin/about-us/items/${item.id}/move`,
                {
                    direction,
                }
            );

            await fetchAboutUs();
        } catch (err: any) {
            handleError(
                err,
                "Unable to change item order. Please try again."
            );
        } finally {
            setMovingItemId(null);
        }
    };

    const handleFeaturedChange = (
        id: number,
        field:
            | "title"
            | "body"
            | "link",
        value: string
    ) => {
        setFeaturedCards(
            (cards) =>
                cards.map((card) =>
                    card.id === id
                        ? {
                            ...card,
                            [field]:
                            value,
                        }
                        : card
                )
        );
    };

    const handleFeaturedImageChange = (
        id: number,
        file: File | null
    ) => {
        setFeaturedCards(
            (cards) =>
                cards.map((card) =>
                    card.id === id
                        ? {
                            ...card,
                            imageFile:
                            file,
                        }
                        : card
                )
        );
    };

    const handleMoveFeatured = async (
        card: PageFeatureCard,
        direction: "up" | "down"
    ) => {
        try {
            setMovingFeaturedId(
                card.id
            );

            setError("");
            setSuccess("");

            await api.post(
                `/admin/page-feature-cards/${card.id}/move`,
                {
                    direction,
                }
            );

            if (aboutUs?.page_id) {
                await fetchFeaturedCards(
                    aboutUs.page_id
                );
            }

            setSuccess(
                "Featured card order updated successfully."
            );
        } catch (err: any) {
            handleError(
                err,
                "Unable to change Featured card order. Please try again."
            );
        } finally {
            setMovingFeaturedId(null);
        }
    };

    const handleSaveFeatured = async () => {
        try {
            setSavingFeatured(true);
            setError("");
            setSuccess("");

            for (const card of featuredCards) {
                const formData = new FormData();

                formData.append(
                    "body",
                    card.body
                );

                formData.append(
                    "link",
                    card.link || ""
                );

                formData.append(
                    "display_order",
                    String(card.display_order)
                );

                if (card.imageFile) {
                    formData.append(
                        "image",
                        card.imageFile
                    );
                }

                formData.append(
                    "_method",
                    "PUT"
                );

                await api.post(
                    `/admin/page-feature-cards/${card.id}`,
                    formData
                );
            }

            if (aboutUs?.page_id) {
                await fetchFeaturedCards(
                    aboutUs.page_id
                );
            }

            setSuccess(
                "Featured content saved successfully."
            );
        } catch (err: any) {
            console.error(
                "Failed to save About Us Featured cards:",
                err
            );

            handleError(
                err,
                "Unable to save Featured content. Please try again."
            );
        } finally {
            setSavingFeatured(false);
        }
    };

    const handleError = (
        err: any,
        fallback: string
    ) => {
        console.error(err);

        const validationErrors =
            err.response?.data?.errors;

        if (validationErrors) {
            const firstError =
                Object.values(
                    validationErrors
                )
                    .flat()
                    .find(
                        (
                            validationError
                        ): validationError is string =>
                            typeof validationError ===
                            "string"
                    );

            setError(
                firstError ||
                fallback
            );
        } else {
            setError(
                err.response?.data?.message ||
                fallback
            );
        }
    };

    const renderItems = (
        items: AboutUsItem[],
        type:
            | "why_choose"
            | "different"
    ) => {
        return (
            <div className="admin-about-items">
                {items.length === 0 ? (
                    <p>
                        No items added yet.
                    </p>
                ) : (
                    <div className="admin-users-table">
                        <table>
                            <thead>
                            <tr>
                                <th>
                                    Icon
                                </th>
                                <th>
                                    Item
                                </th>
                                <th>
                                    Actions
                                </th>
                            </tr>
                            </thead>

                            <tbody>
                            {items.map(
                                (
                                    item,
                                    index
                                ) => (
                                    <tr
                                        key={
                                            item.id
                                        }
                                    >
                                        <td>
                                            <div className="admin-about-table-icon">
                                                {item.icon ? (
                                                    <img
                                                        src={getFileUrl(
                                                            item.icon
                                                        )}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <span>
                                                            —
                                                        </span>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            {
                                                item.body
                                            }
                                        </td>

                                        <td>
                                            <div className="admin-user-actions">
                                                <button
                                                    type="button"
                                                    className="admin-table-button"
                                                    onClick={() =>
                                                        handleMoveItem(
                                                            item,
                                                            "up"
                                                        )
                                                    }
                                                    disabled={
                                                        index ===
                                                        0 ||
                                                        movingItemId !==
                                                        null ||
                                                        deletingItemId !==
                                                        null
                                                    }
                                                    aria-label="Move item up"
                                                >
                                                    ↑
                                                </button>

                                                <button
                                                    type="button"
                                                    className="admin-table-button"
                                                    onClick={() =>
                                                        handleMoveItem(
                                                            item,
                                                            "down"
                                                        )
                                                    }
                                                    disabled={
                                                        index ===
                                                        items.length -
                                                        1 ||
                                                        movingItemId !==
                                                        null ||
                                                        deletingItemId !==
                                                        null
                                                    }
                                                    aria-label="Move item down"
                                                >
                                                    ↓
                                                </button>

                                                <button
                                                    type="button"
                                                    className="admin-table-button"
                                                    onClick={() =>
                                                        handleEditItem(
                                                            item
                                                        )
                                                    }
                                                    disabled={
                                                        movingItemId !==
                                                        null ||
                                                        deletingItemId !==
                                                        null
                                                    }
                                                >
                                                    Edit
                                                </button>

                                                <button
                                                    type="button"
                                                    className="admin-table-button admin-table-button-danger"
                                                    onClick={() =>
                                                        handleDeleteItem(
                                                            item
                                                        )
                                                    }
                                                    disabled={
                                                        deletingItemId ===
                                                        item.id ||
                                                        movingItemId !==
                                                        null
                                                    }
                                                >
                                                    {deletingItemId ===
                                                    item.id
                                                        ? "Deleting..."
                                                        : "Delete"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/*<div className="admin-about-section-actions">*/}
                {/*    <button*/}
                {/*        type="button"*/}
                {/*        className="admin-primary-button"*/}
                {/*        onClick={() =>*/}
                {/*            handleAddItem(*/}
                {/*                type*/}
                {/*            )*/}
                {/*        }*/}
                {/*        disabled={*/}
                {/*            movingItemId !==*/}
                {/*            null ||*/}
                {/*            deletingItemId !==*/}
                {/*            null*/}
                {/*        }*/}
                {/*    >*/}
                {/*        Add item*/}
                {/*    </button>*/}
                {/*</div>*/}
            </div>
        );
    };

    const renderFeaturedCards = () => {
        if (featuredCards.length === 0) {
            return (
                <p>
                    No About Us feature cards found.
                </p>
            );
        }

        return (
            <div className="admin-feature-cards">
                {featuredCards.map((card, index) => (
                    <div
                        className="admin-feature-card"
                        key={card.id}
                    >
                        <div className="admin-feature-card-header">
                            <div>
                                <h3>
                                    {card.title}
                                </h3>

                                <span>
                                Card {index + 1}
                            </span>
                            </div>

                            <div className="admin-feature-card-order">
                                <button
                                    type="button"
                                    className="admin-table-button"
                                    onClick={() =>
                                        handleMoveFeatured(
                                            card,
                                            "up"
                                        )
                                    }
                                    disabled={
                                        index === 0 ||
                                        movingFeaturedId !== null ||
                                        savingFeatured
                                    }
                                    aria-label={`Move ${card.title} up`}
                                >
                                    ↑
                                </button>

                                <button
                                    type="button"
                                    className="admin-table-button"
                                    onClick={() =>
                                        handleMoveFeatured(
                                            card,
                                            "down"
                                        )
                                    }
                                    disabled={
                                        index ===
                                        featuredCards.length - 1 ||
                                        movingFeaturedId !== null ||
                                        savingFeatured
                                    }
                                    aria-label={`Move ${card.title} down`}
                                >
                                    ↓
                                </button>
                            </div>
                        </div>

                        <div className="admin-feature-card-content">

                            <div className="form-group">
                                <label
                                    htmlFor={`about-feature-image-${card.id}`}
                                >
                                    Image
                                </label>

                                <div className="admin-feature-image-preview">
                                    {card.imageFile ? (
                                        <img
                                            src={URL.createObjectURL(
                                                card.imageFile
                                            )}
                                            alt={`${card.title} feature`}
                                        />
                                    ) : card.image ? (
                                        <img
                                            src={getFileUrl(
                                                card.image
                                            )}
                                            alt={`${card.title} feature`}
                                        />
                                    ) : (
                                        <span>
                                        No image uploaded
                                    </span>
                                    )}
                                </div>

                                <input
                                    id={`about-feature-image-${card.id}`}
                                    type="file"
                                    accept="image/*"
                                    onChange={(event) =>
                                        handleFeaturedImageChange(
                                            card.id,
                                            event.target.files?.[0] ||
                                            null
                                        )
                                    }
                                    disabled={
                                        savingFeatured
                                    }
                                />
                            </div>

                            <div className="form-group">
                                <label
                                    htmlFor={`about-feature-body-${card.id}`}
                                >
                                    Body
                                </label>

                                <textarea
                                    id={`about-feature-body-${card.id}`}
                                    value={card.body}
                                    onChange={(event) =>
                                        handleFeaturedChange(
                                            card.id,
                                            "body",
                                            event.target.value
                                        )
                                    }
                                    rows={5}
                                    disabled={
                                        savingFeatured
                                    }
                                />
                            </div>

                            <div className="form-group">
                                <label
                                    htmlFor={`about-feature-link-${card.id}`}
                                >
                                    Link
                                </label>

                                <input
                                    id={`about-feature-link-${card.id}`}
                                    type="text"
                                    value={
                                        card.link || ""
                                    }
                                    onChange={(event) =>
                                        handleFeaturedChange(
                                            card.id,
                                            "link",
                                            event.target.value
                                        )
                                    }
                                    placeholder="Optional"
                                    disabled={
                                        savingFeatured
                                    }
                                />
                            </div>

                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="admin-page">
            <AdminHeader />

            <div className="admin-body">
                <AdminSidebar />

                <main className="admin-content">
                    <div className="admin-users">
                        <div className="admin-page-header">
                            <div>
                                <h1>
                                    About Us
                                </h1>

                                <p>
                                    Manage the content
                                    displayed on the
                                    About Us page.
                                </p>
                            </div>
                        </div>

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="form-success">
                                {success}
                            </div>
                        )}

                        {loading ? (
                            <p>
                                Loading About Us...
                            </p>
                        ) : (
                            <>
                                <section className="admin-content-section">
                                    <div className="admin-page-header">
                                        <div>
                                            <h2>
                                                About Us
                                            </h2>

                                            <p>
                                                Manage the
                                                introductory
                                                About Us
                                                content.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={
                                                handleSaveContent
                                            }
                                            disabled={
                                                savingContent
                                            }
                                        >
                                            {savingContent
                                                ? "Saving..."
                                                : "Save changes"}
                                        </button>
                                    </div>

                                    <div className="admin-about-main">
                                        <div className="form-group">
                                            <label>
                                                Image
                                            </label>

                                            <div className="admin-about-main-image">
                                                {image ? (
                                                    <img
                                                        src={URL.createObjectURL(
                                                            image
                                                        )}
                                                        alt="New About Us"
                                                    />
                                                ) : aboutUs?.image ? (
                                                    <img
                                                        src={getFileUrl(
                                                            aboutUs.image
                                                        )}
                                                        alt="About Us"
                                                    />
                                                ) : (
                                                    <span>
                                                        No image uploaded
                                                    </span>
                                                )}
                                            </div>

                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(
                                                    event
                                                ) =>
                                                    setImage(
                                                        event
                                                            .target
                                                            .files?.[0] ||
                                                        null
                                                    )
                                                }
                                                disabled={
                                                    savingContent
                                                }
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="about-heading">
                                                Heading / intro
                                            </label>

                                            <textarea
                                                id="about-heading"
                                                value={
                                                    heading
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setHeading(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                rows={5}
                                                disabled={
                                                    savingContent
                                                }
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="about-ending">
                                                Ending
                                            </label>

                                            <textarea
                                                id="about-ending"
                                                value={
                                                    ending
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setEnding(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                rows={4}
                                                disabled={
                                                    savingContent
                                                }
                                            />
                                        </div>
                                    </div>
                                </section>

                                <section className="admin-content-section">
                                    <div className="admin-page-header">
                                        <div>
                                            <h2>
                                                Why Choose Safari?
                                            </h2>

                                            <p>
                                                Manage the Why Choose Safari items.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={() =>
                                                handleAddItem(
                                                    "why_choose"
                                                )
                                            }
                                            disabled={
                                                movingItemId !==
                                                null ||
                                                deletingItemId !==
                                                null
                                            }
                                        >
                                            Add item
                                        </button>
                                    </div>

                                    {renderItems(
                                        whyChooseItems,
                                        "why_choose"
                                    )}
                                </section>

                                <section className="admin-content-section">
                                    <div className="admin-page-header">
                                        <div>
                                            <h2>
                                                How Is Safari
                                                Different From
                                                Imitation Vinegar?
                                            </h2>

                                            <p>
                                                Manage the comparison items.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={() =>
                                                handleAddItem(
                                                    "different"
                                                )
                                            }
                                            disabled={
                                                movingItemId !==
                                                null ||
                                                deletingItemId !==
                                                null
                                            }
                                        >
                                            Add item
                                        </button>
                                    </div>

                                    {renderItems(
                                        differentItems,
                                        "different"
                                    )}
                                </section>

                                <section className="admin-content-section">
                                    <div className="admin-page-header">
                                        <div>
                                            <h2>
                                                Featured
                                            </h2>

                                            <p>
                                                Manage the three featured cards displayed on the About Us page.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={
                                                handleSaveFeatured
                                            }
                                            disabled={
                                                savingFeatured ||
                                                movingFeaturedId !==
                                                null
                                            }
                                        >
                                            {savingFeatured
                                                ? "Saving..."
                                                : "Save Changes"}
                                        </button>
                                    </div>

                                    {renderFeaturedCards()}
                                </section>
                            </>
                        )}
                    </div>

                    {showItemModal && (
                        <AboutUsItemModal
                            item={
                                selectedItem
                            }
                            type={
                                modalType
                            }
                            onClose={
                                handleCloseModal
                            }
                            onSaved={
                                handleItemSaved
                            }
                        />
                    )}
                </main>
            </div>
        </div>
    );
};

export default AdminAboutUs;
